import { useState } from "react";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

export default function ImageToPdf() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  // Handle image uploads
  const handleFiles = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(file =>
      file.type.startsWith("image/")
    );
    setImages(selectedFiles);
  };

  // Remove single image
  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Convert images to PDF
  const convertToPDF = async () => {
    if (images.length === 0) {
      alert("Please upload at least one image");
      return;
    }
    try {
      setLoading(true);
      const pdfDoc = await PDFDocument.create();
      for (const imgFile of images) {
        const bytes = await imgFile.arrayBuffer();
        let img;
        if (imgFile.type === "image/png") {
          img = await pdfDoc.embedPng(bytes);
        } else {
          img = await pdfDoc.embedJpg(bytes);
        }
        const page = pdfDoc.addPage([img.width, img.height]);
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
      }
      const pdfBytes = await pdfDoc.save();
      saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "images.pdf");
      setSuccess("PDF created and downloaded successfully!");
    } catch (err) {
      console.error(err);
      alert("Error converting images to PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Image to PDF Converter Online"
        description="Convert JPG and PNG images to PDF documents instantly online for free."
        keywords="image to pdf, convert image to pdf online, jpg to pdf, png to pdf, online pdf converter"
        url="https://online-tools-center.vercel.app/image-to-pdf"
      />

      <Navbar />

      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-lg border-0 rounded-4 p-4">
              {/* Hero */}
              <div className="text-center mb-4">
                <h1 className="fw-bold mb-3">Image to PDF</h1>
                <p className="text-muted">
                  Convert multiple JPG or PNG images into a single PDF document instantly.
                </p>
              </div>

              {/* File input */}
              <div className="mb-3">
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  className="form-control"
                  onChange={handleFiles}
                />
              </div>

              {/* Image previews */}
              {images.length > 0 && (
                <div className="mb-3">
                  <h5 className="fw-semibold mb-2">Uploaded Images</h5>
                  <div className="d-flex flex-wrap gap-3">
                    {images.map((img, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={URL.createObjectURL(img)}
                          alt={`preview-${index}`}
                          style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }}
                        />
                        <button
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          onClick={() => removeImage(index)}
                          style={{ borderRadius: "50%" }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action button */}
              <div className="text-center">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={convertToPDF}
                  disabled={loading}
                >
                  {loading ? "Converting..." : "Convert to PDF"}
                </button>
              </div>

              {success && (
                <div className="alert alert-success mt-3 text-center">{success}</div>
              )}

              {/* SEO Content */}
              <div className="mt-5">
                <h2 className="fw-bold mb-3">Free Online Image to PDF Converter</h2>
                <p>
                  This tool allows you to convert multiple images (PNG, JPG) into a single PDF file quickly and securely in your browser without uploading files to a server.
                </p>

                <h2 className="fw-bold mt-5 mb-3">Features</h2>
                <ul>
                  <li>Convert multiple images into PDF</li>
                  <li>Supports JPG and PNG formats</li>
                  <li>Preview images before conversion</li>
                  <li>Browser-based and secure</li>
                  <li>Download PDF instantly</li>
                  <li>Mobile responsive</li>
                </ul>

                <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>
                <div className="mb-4">
                  <h5>Is this tool free?</h5>
                  <p>Yes, this online image to PDF converter is completely free.</p>
                </div>
                <div className="mb-4">
                  <h5>Are my images secure?</h5>
                  <p>Yes, all processing happens locally in your browser. Images are not uploaded to any server.</p>
                </div>
                <div className="mb-4">
                  <h5>Can I convert multiple images at once?</h5>
                  <p>Yes, you can select multiple JPG or PNG images and convert them into a single PDF file.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}